import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api } from '@/client/api';
import { Page, PageHeader } from '@/client/components/AppShell';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  Select,
  Sheet,
  SkeletonList,
  Textarea,
  Toggle,
} from '@/client/components/ui';
import { useSaveVolunteer, useWorkspace } from '@/client/store';
import { addWeeks, compareDates, formatShort } from '@/domain/dates';
import { computeVolunteerStats, type VolunteerStats } from '@/domain/stats';
import { VOLUNTEER_STATUSES, type Volunteer, type VolunteerStatus } from '@/shared/types';

/** The roster, ordered by who is due next. */
export function Volunteers() {
  const { data: workspace, isPending } = useWorkspace();
  const [editing, setEditing] = useState<Volunteer | 'new' | null>(null);

  const stats = useMemo(() => {
    if (!workspace) return [];
    return computeVolunteerStats(
      workspace.volunteers,
      workspace.assignments,
      workspace.settings,
      workspace.today,
    ).sort((a, b) => {
      // Anyone unavailable sinks to the bottom; the rest sort by who is due.
      if (a.eligibleToday !== b.eligibleToday) return a.eligibleToday ? -1 : 1;
      return a.credit - b.credit;
    });
  }, [workspace]);

  return (
    <>
      <PageHeader
        title="Volunteers"
        subtitle={workspace ? `${workspace.volunteers.length} on the roster` : undefined}
        actions={
          <Button variant="primary" onClick={() => setEditing('new')} disabled={!workspace}>
            Add
          </Button>
        }
      />

      <Page>
        {isPending || !workspace ? (
          <SkeletonList rows={5} />
        ) : stats.length === 0 ? (
          <Card>
            <EmptyState
              title="No volunteers yet"
              description="Add the people who can drive, and the tool will work out a fair rotation."
              action={
                <Button variant="primary" onClick={() => setEditing('new')}>
                  Add the first volunteer
                </Button>
              }
            />
          </Card>
        ) : (
          <>
            <p className="px-1 text-xs text-ink-faint">
              Sorted by who is due next. "In window" is credit earned over the last{' '}
              {workspace.settings.rollingWindowWeeks} weeks — the number the ranker sorts on.
            </p>
            <ul className="flex flex-col gap-2">
              {stats.map((row) => {
                const volunteer = workspace.volunteers.find((v) => v.id === row.volunteerId)!;
                return (
                  <VolunteerRow
                    key={row.volunteerId}
                    stats={row}
                    volunteer={volunteer}
                    onOpen={() => setEditing(volunteer)}
                  />
                );
              })}
            </ul>
          </>
        )}
      </Page>

      {editing && workspace ? (
        <VolunteerEditor
          volunteer={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

function VolunteerRow({
  stats,
  volunteer,
  onOpen,
}: {
  stats: VolunteerStats;
  volunteer: Volunteer;
  onOpen: () => void;
}) {
  const days = [
    volunteer.availableThursday ? 'Thu' : null,
    volunteer.availableSunday ? 'Sun' : null,
  ].filter(Boolean);

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="card tap-target flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-sunken"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{volunteer.name}</span>
          <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
            {!stats.eligibleToday ? (
              <Badge tone="caution">
                {volunteer.status === 'Removed'
                  ? 'Removed'
                  : volunteer.pausedUntil
                    ? `Paused until ${formatShort(volunteer.pausedUntil)}`
                    : 'Paused'}
              </Badge>
            ) : null}
            {days.length > 0 ? (
              <Badge>{days.join(' + ')}</Badge>
            ) : (
              <Badge tone="critical">No days</Badge>
            )}
            {stats.bailed > 0 ? <Badge tone="critical">{stats.bailed} bailed</Badge> : null}
            {stats.upcoming > 0 ? <Badge tone="brand">{stats.upcoming} upcoming</Badge> : null}
          </span>
        </span>

        <span className="shrink-0 text-right">
          <span className="block text-sm font-semibold tabular-nums">{stats.credit}</span>
          <span className="block text-[0.6875rem] text-ink-faint">in window</span>
        </span>
      </button>
    </li>
  );
}

function VolunteerEditor({
  volunteer,
  onClose,
}: {
  volunteer: Volunteer | null;
  onClose: () => void;
}) {
  const save = useSaveVolunteer();
  const isNew = volunteer === null;

  // Offer the current rolling average so a newcomer lands mid-rotation rather
  // than at the top of every list for a month (spec 3.3). Offered for an
  // existing volunteer too, as the number the "use the average" button snaps to.
  const suggestedSeed = useQuery({
    queryKey: ['suggested-seed', volunteer?.id ?? 'new'],
    queryFn: () => api.suggestedSeed(volunteer?.id),
    staleTime: 60_000,
  });

  const [draft, setDraft] = useState(() => ({
    name: volunteer?.name ?? '',
    email: volunteer?.email ?? '',
    phone: volunteer?.phone ?? '',
    availableThursday: volunteer?.availableThursday ?? true,
    availableSunday: volunteer?.availableSunday ?? true,
    status: (volunteer?.status ?? 'Active') as VolunteerStatus,
    pausedUntil: volunteer?.pausedUntil ?? '',
    notes: volunteer?.notes ?? '',
  }));

  // `null` means "whatever the suggestion turns out to be": a newcomer's field
  // fills itself in when the average arrives, but stops following it the moment
  // the coordinator types their own number.
  const [seedInput, setSeedInput] = useState<string | null>(
    volunteer ? String(volunteer.seedCredit) : null,
  );

  const suggested = suggestedSeed.data?.seedCredit;
  // While the average is still on its way the field is blank; if it never
  // arrives, zero stands in, so a failed suggestion cannot wedge the editor.
  const seedValue =
    seedInput ?? (suggested !== undefined ? String(suggested) : suggestedSeed.isPending ? '' : '0');
  const seedCredit = Number(seedValue);
  const seedIsValid = seedValue.trim() !== '' && Number.isFinite(seedCredit) && seedCredit >= 0;

  // A write queued while offline stays pending until the connection returns.
  // The sheet has nothing left to say at that point, so let it close and let
  // the header's sync indicator carry the write.
  useEffect(() => {
    if (save.isPaused) onClose();
  }, [save.isPaused, onClose]);

  const pauseLooksLong = Boolean(
    volunteer?.pausedUntil &&
      compareDates(volunteer.pausedUntil, addWeeks(new Date().toISOString().slice(0, 10), 4)) > 0,
  );

  const submit = () => {
    const trimmed = draft.name.trim();
    if (!trimmed || !seedIsValid) return;

    // Re-stamp the seed date whenever the number itself moves, so a changed
    // seed ages out of the rolling window from today rather than from whenever
    // the volunteer first joined.
    const changedSeed = isNew || seedCredit !== volunteer?.seedCredit;

    save.mutate(
      {
        ...(volunteer?.id ? { id: volunteer.id } : {}),
        name: trimmed,
        email: draft.email.trim(),
        phone: draft.phone.trim(),
        availableThursday: draft.availableThursday,
        availableSunday: draft.availableSunday,
        status: draft.status,
        pausedUntil: draft.pausedUntil || null,
        notes: draft.notes,
        seedCredit,
        seededOn: changedSeed
          ? new Date().toISOString().slice(0, 10)
          : (volunteer?.seededOn ?? null),
      },
      // Closing regardless would throw the rejection away: the notice below is
      // rendered into this sheet, so dismissing it on submit is what turns a
      // failed save into "nothing happened".
      { onSuccess: onClose },
    );
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title={isNew ? 'Add volunteer' : draft.name || 'Volunteer'}
      footer={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            variant="primary"
            className="flex-1"
            busy={save.isPending}
            disabled={!draft.name.trim() || !seedIsValid || save.isPending}
            onClick={submit}
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {save.isError ? <ErrorNotice error={save.error} /> : null}

        <Field label="Name" hint="A couple who drive together can be one entry.">
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={draft.name}
              autoComplete="name"
              placeholder="Josh & Chanda"
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />
          )}
        </Field>

        <Field label="Email" hint="Used for the calendar invite. Gmail works best.">
          {({ id, describedBy }) => (
            <Input
              id={id}
              type="email"
              inputMode="email"
              autoComplete="email"
              aria-describedby={describedBy}
              value={draft.email}
              onChange={(event) => setDraft({ ...draft, email: event.target.value })}
            />
          )}
        </Field>

        <Field label="Phone" hint="For the weekly “still good?” text.">
          {({ id, describedBy }) => (
            <Input
              id={id}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              aria-describedby={describedBy}
              value={draft.phone}
              onChange={(event) => setDraft({ ...draft, phone: event.target.value })}
            />
          )}
        </Field>

        <div className="flex flex-col gap-1">
          <Toggle
            label="Available Thursdays"
            checked={draft.availableThursday}
            onChange={(availableThursday) => setDraft({ ...draft, availableThursday })}
          />
          <Toggle
            label="Available Sundays"
            checked={draft.availableSunday}
            onChange={(availableSunday) => setDraft({ ...draft, availableSunday })}
          />
        </div>

        <Field label="Status">
          {({ id }) => (
            <Select
              id={id}
              value={draft.status}
              onChange={(event) =>
                setDraft({ ...draft, status: event.target.value as VolunteerStatus })
              }
            >
              {VOLUNTEER_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field
          label="Paused until"
          hint="Optional. They come back into the rotation automatically after this date."
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              type="date"
              aria-describedby={describedBy}
              value={draft.pausedUntil}
              onChange={(event) => setDraft({ ...draft, pausedUntil: event.target.value })}
            />
          )}
        </Field>

        <Field
          label="Starting credit"
          hint={
            isNew
              ? 'Where they join the rotation. The current average keeps them out of the top of every list; a lower number brings them up sooner.'
              : 'Changing this re-seeds them, and the new number ages out of the rolling window from today.'
          }
          error={seedIsValid ? undefined : 'Enter a number, zero or more.'}
        >
          {({ id, describedBy }) => (
            <div className="flex items-center gap-2">
              <Input
                id={id}
                type="number"
                inputMode="decimal"
                min={0}
                step={0.5}
                aria-describedby={describedBy}
                placeholder={suggestedSeed.isPending ? 'Working out the average…' : ''}
                value={seedValue}
                onChange={(event) => setSeedInput(event.target.value)}
              />
              {suggested !== undefined && seedCredit !== suggested ? (
                <Button
                  variant="secondary"
                  className="shrink-0"
                  onClick={() => setSeedInput(String(suggested))}
                >
                  Average ({suggested})
                </Button>
              ) : null}
            </div>
          )}
        </Field>

        {!isNew && pauseLooksLong ? (
          <p className="rounded-xl bg-brand-soft px-3 py-2 text-xs text-brand-ink">
            This pause is longer than the rolling window. Setting the starting credit back to the
            current average is usually the fairest way to bring them back in.
          </p>
        ) : null}

        <Field label="Notes" hint="e.g. “not comfortable driving at night”.">
          {({ id, describedBy }) => (
            <Textarea
              id={id}
              aria-describedby={describedBy}
              value={draft.notes}
              onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
            />
          )}
        </Field>
      </div>
    </Sheet>
  );
}
